import React, { useState } from 'react';
import { Form, Input, Button, Modal, Typography, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { showToast } from '@/ts/modals';
import { createClientBrand } from '@/ts/retrieve-data';
import '@/scss/add-brand-modal.scss';
import { IAddBrandModalProps } from '@/ts/common-types';

const { Text, Title } = Typography;

interface BrandFormValues {
    brands: string[];
}

const AddBrandModal: React.FC<IAddBrandModalProps> = ({
    clientId,
    visible,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values: BrandFormValues) => {
        setLoading(true);
        try {
            const brandNames = values.brands.filter(name => name && name.trim().length > 0);
            
            if (brandNames.length === 0) {
                throw new Error('Please enter at least one brand name');
            }

            const uniqueNames = new Set(brandNames);
            if (uniqueNames.size !== brandNames.length) {
                throw new Error('Duplicate brand names are not allowed');
            }
            
            await createClientBrand(clientId, { name: brandNames });
            showToast('success', 'Brands added successfully');
            form.resetFields();
            onSuccess();
        } catch (error: any) {
            showToast('danger', error.message || 'Failed to add brands');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setLoading(false);
        onCancel();
    };

    return (
        <Modal
            title={
                <Title level={4} className="m-0 d-flex align-items-center">
                    Add Brands
                </Title>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            maskClosable={false}
            width={600}
            className="brand-modal"
        >
            <div className="mb-3 mt-2">
                <Text type="secondary">
                    Add one or multiple brand names below. Each brand name must be unique.
                </Text>
            </div>
            
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ brands: [''] }}
            >
                <Form.List name="brands">
                    {(fields, { add, remove }) => (
                        <div className="mb-3" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                            {fields.map((field, index) => (
                                <Form.Item
                                    required={false}
                                    key={field.key}
                                    className="mb-2"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Item
                                            {...field}
                                            validateTrigger={['onChange', 'onBlur']}
                                            rules={[
                                                {
                                                    required: true,
                                                    whitespace: true,
                                                    message: "Please enter brand name or remove this field",
                                                },
                                                {
                                                    validator: async (_, value) => {
                                                        if (!value) return;
                                                        const allValues = form.getFieldValue('brands') || [];
                                                        const currentValue = value.trim();
                                                        const duplicateCount = allValues.filter(
                                                            (name: string) => name === currentValue
                                                        ).length;
                                                        
                                                        if (duplicateCount > 1) {
                                                            throw new Error('Brand name must be unique');
                                                        }
                                                    }
                                                }
                                            ]}
                                            className="flex-grow-1 mb-0"
                                        >
                                            <Input 
                                                placeholder={`Brand name ${index + 1}`}
                                                size="large"
                                                className="brand-input"
                                                autoFocus={index === fields.length - 1}
                                                style={{
                                                    padding: '8px 12px',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '6px'
                                                }}
                                            />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <MinusCircleOutlined
                                                className="remove-brand"
                                                onClick={() => remove(field.name)}
                                            />
                                        )}
                                    </div>
                                </Form.Item>
                            ))}
                            
                            <Form.Item className="mb-0">
                                <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    size="large"
                                    className="d-flex align-items-center justify-content-center mt-2"
                                    style={{ height: 'auto', padding: '12px' }}
                                >
                                    <PlusOutlined className="fs-6 me-2" />
                                    Add Another Brand
                                </Button>
                            </Form.Item>
                        </div>
                    )}
                </Form.List>

                <Divider className="mx-3" />

                <div className="d-flex justify-content-end gap-3 px-3 pb-2 mb-2">
                    <Button 
                        size="large" 
                        onClick={handleCancel}
                        className="px-4"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                        size="large"
                        className="px-4"
                    >
                        Add Brands
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AddBrandModal; 